import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarRange,
  CheckCircle2,
  FileCheck2,
  Landmark,
  Save,
  Settings2,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Card,
  InlineNotice,
  Money,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

type TaxpayerType =
  | "UNSET"
  | "HOUSEHOLD_BUSINESS"
  | "INDIVIDUAL_BUSINESS"
  | "SOLE_PROPRIETORSHIP"
  | "LIMITED_LIABILITY_COMPANY"
  | "JOINT_STOCK_COMPANY"
  | "OTHER";

type VatMethod = "UNSET" | "CREDIT" | "DIRECT" | "NOT_SUBJECT";
type FilingCadence =
  | "UNSET"
  | "MONTHLY"
  | "QUARTERLY"
  | "PER_OCCURRENCE"
  | "HOUSEHOLD_RULE";
type TaxPeriodStatus = "OPEN" | "REVIEW" | "DECLARED" | "LOCKED";
type TaxPeriodType = "MONTH" | "QUARTER" | "YEAR" | "OTHER";
type WorkspaceStatus =
  | "INCOMPLETE"
  | "BLOCKED_BY_COMPLIANCE"
  | "REVIEW_REQUIRED"
  | "READY";

interface TaxProfile {
  taxpayerType: TaxpayerType;
  vatMethod: VatMethod;
  filingCadence: FilingCadence;
  businessSector?: string | null;
  configured: boolean;
}

interface ComplianceSummary {
  recordedRevenue: number;
  grossRevenue?: number;
  returnedRevenue?: number;
  transactionCount: number;
  returnCount: number;
  workspaceStatus?: WorkspaceStatus;
  issues?: Array<{ code: string; severity: string; count?: number }>;
  businessProfile?: {
    configured: boolean;
    completed: number;
    total: number;
    percent: number;
    taxCodePresent: boolean;
  };
  dataQuality: {
    pendingPayments: number;
    negativeStock: number;
    status: string;
  };
  taxpayerProfile: TaxProfile;
  legalRules: {
    verifiedCount: number;
    versions: Array<{ code: string; version: string }>;
    status: string;
  };
  taxEstimate: null;
  notice: string;
}

interface ComplianceRule {
  id: string;
  code: string;
  version: string;
  name: string;
  ruleType: string;
  legalReference?: string | null;
  sourceUrl?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

interface TaxPeriod {
  id: string;
  code: string;
  periodType: string;
  startDate: string;
  endDate: string;
  status: TaxPeriodStatus;
  declarationReference?: string | null;
}

const taxpayerLabels: Record<TaxpayerType, string> = {
  UNSET: "Chưa thiết lập",
  HOUSEHOLD_BUSINESS: "Hộ kinh doanh",
  INDIVIDUAL_BUSINESS: "Cá nhân kinh doanh",
  SOLE_PROPRIETORSHIP: "Doanh nghiệp tư nhân",
  LIMITED_LIABILITY_COMPANY: "Công ty TNHH",
  JOINT_STOCK_COMPANY: "Công ty cổ phần",
  OTHER: "Khác",
};

const vatLabels: Record<VatMethod, string> = {
  UNSET: "Chưa thiết lập",
  CREDIT: "Khấu trừ",
  DIRECT: "Trực tiếp",
  NOT_SUBJECT: "Không thuộc đối tượng VAT",
};

const cadenceLabels: Record<FilingCadence, string> = {
  UNSET: "Chưa thiết lập",
  MONTHLY: "Theo tháng",
  QUARTERLY: "Theo quý",
  PER_OCCURRENCE: "Theo từng lần phát sinh",
  HOUSEHOLD_RULE: "Theo quy định dành cho hộ/cá nhân kinh doanh",
};

const periodStatusLabels: Record<TaxPeriodStatus, string> = {
  OPEN: "Đang mở",
  REVIEW: "Đang rà soát",
  DECLARED: "Đã kê khai",
  LOCKED: "Đã khóa",
};

const workspaceLabels: Record<WorkspaceStatus, string> = {
  INCOMPLETE: "Thiết lập chưa hoàn tất",
  BLOCKED_BY_COMPLIANCE: "Chờ căn cứ VERIFIED",
  REVIEW_REQUIRED: "Cần đối soát dữ liệu",
  READY: "Sẵn sàng rà soát",
};

function nextPeriodStatus(status: TaxPeriodStatus) {
  if (status === "OPEN") return "REVIEW" as const;
  if (status === "REVIEW") return "DECLARED" as const;
  if (status === "DECLARED") return "LOCKED" as const;
  return null;
}

function statusTone(status: TaxPeriodStatus) {
  if (status === "LOCKED") return "success" as const;
  if (status === "DECLARED") return "info" as const;
  if (status === "REVIEW") return "warning" as const;
  return "neutral" as const;
}

function workspaceTone(status: WorkspaceStatus) {
  if (status === "READY") return "success" as const;
  if (status === "REVIEW_REQUIRED") return "warning" as const;
  return "warning" as const;
}

function dateOnly(value: string) {
  return value.slice(0, 10);
}

function pad2(value: number) {
  return String(value).padStart(2, "0");
}

function monthEnd(year: number, month: number) {
  const day = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function periodPreview(
  type: TaxPeriodType,
  year: number,
  month: number,
  quarter: number,
  customStart: string,
  customEnd: string,
) {
  if (type === "MONTH") {
    return {
      code: `${year}-M${pad2(month)}`,
      startDate: `${year}-${pad2(month)}-01`,
      endDate: monthEnd(year, month),
    };
  }
  if (type === "QUARTER") {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    return {
      code: `${year}-Q${quarter}`,
      startDate: `${year}-${pad2(startMonth)}-01`,
      endDate: monthEnd(year, endMonth),
    };
  }
  if (type === "YEAR") {
    return {
      code: `${year}-Y`,
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
    };
  }
  return {
    code:
      customStart && customEnd
        ? `CUSTOM-${customStart.replaceAll("-", "")}-${customEnd.replaceAll("-", "")}`
        : "Tự tạo sau khi chọn ngày",
    startDate: customStart,
    endDate: customEnd,
  };
}

export function TaxPage() {
  const auth = useAuth();
  const role = auth.session?.user.role;
  const canEditProfile = role === "OWNER" && !auth.demo && navigator.onLine;
  const canManagePeriods =
    (role === "OWNER" || role === "ACCOUNTANT") &&
    !auth.demo &&
    navigator.onLine;

  const now = new Date();
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [profile, setProfile] = useState<TaxProfile>({
    taxpayerType: "UNSET",
    vatMethod: "UNSET",
    filingCadence: "UNSET",
    businessSector: "",
    configured: false,
  });
  const [rules, setRules] = useState<ComplianceRule[]>([]);
  const [periods, setPeriods] = useState<TaxPeriod[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const [periodType, setPeriodType] = useState<TaxPeriodType>("MONTH");
  const [periodYear, setPeriodYear] = useState(now.getFullYear());
  const [periodMonth, setPeriodMonth] = useState(now.getMonth() + 1);
  const [periodQuarter, setPeriodQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  async function reload() {
    if (auth.demo || !navigator.onLine) return;
    const [summaryData, profileData, ruleData, periodData] = await Promise.all([
      api<ComplianceSummary>("/compliance/summary"),
      api<TaxProfile>("/compliance/profile"),
      api<{ rules: ComplianceRule[] }>("/compliance/rules/active"),
      api<TaxPeriod[]>("/compliance/periods"),
    ]);
    setSummary(summaryData);
    setProfile(profileData);
    setRules(ruleData.rules);
    setPeriods(periodData);
  }

  useEffect(() => {
    void reload().catch(() => {
      setMessage("Không thể tải đầy đủ Tax & Compliance Workspace.");
    });
  }, [auth.demo]);

  const workspaceStatus: WorkspaceStatus =
    summary?.workspaceStatus ??
    (profile.configured && rules.length > 0 ? "READY" : "INCOMPLETE");

  const preview = useMemo(
    () =>
      periodPreview(
        periodType,
        periodYear,
        periodMonth,
        periodQuarter,
        customStart,
        customEnd,
      ),
    [periodType, periodYear, periodMonth, periodQuarter, customStart, customEnd],
  );

  async function saveProfile(event: React.FormEvent) {
    event.preventDefault();
    if (!canEditProfile) return;
    setBusy(true);
    setMessage("");
    try {
      const updated = await api<TaxProfile>("/compliance/profile", {
        method: "PUT",
        body: JSON.stringify({
          taxpayerType: profile.taxpayerType,
          vatMethod: profile.vatMethod,
          filingCadence: profile.filingCadence,
          businessSector: profile.businessSector?.trim() || null,
        }),
      });
      setProfile(updated);
      setMessage("Đã cập nhật hồ sơ thuế và ghi nhận audit trail.");
      await reload();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Không thể cập nhật hồ sơ thuế.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function createPeriod(event: React.FormEvent) {
    event.preventDefault();
    if (!canManagePeriods || !profile.configured) return;
    setBusy(true);
    setMessage("");
    try {
      const body =
        periodType === "MONTH"
          ? { periodType, year: periodYear, month: periodMonth }
          : periodType === "QUARTER"
            ? { periodType, year: periodYear, quarter: periodQuarter }
            : periodType === "YEAR"
              ? { periodType, year: periodYear }
              : {
                  periodType,
                  startDate: customStart,
                  endDate: customEnd,
                };

      await api<TaxPeriod>("/compliance/periods", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setCustomStart("");
      setCustomEnd("");
      setMessage(`Đã tạo kỳ ${preview.code} ở trạng thái OPEN.`);
      await reload();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Không thể tạo kỳ thuế.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function transitionPeriod(period: TaxPeriod) {
    const targetStatus = nextPeriodStatus(period.status);
    if (!targetStatus || !canManagePeriods) return;
    setBusy(true);
    setMessage("");
    try {
      await api<TaxPeriod>(`/compliance/periods/${period.id}/transition`, {
        method: "POST",
        body: JSON.stringify({
          targetStatus,
          reason: `Chuyển kỳ ${period.code}: ${period.status} → ${targetStatus}`,
        }),
      });
      setMessage(
        `Đã chuyển kỳ ${period.code} sang ${periodStatusLabels[targetStatus]}.`,
      );
      await reload();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Không thể chuyển trạng thái kỳ thuế.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        back
        title="Thuế & sổ sách"
        description="Hồ sơ thuế, rule pháp lý theo phiên bản và kỳ kê khai có kiểm soát"
      />

      {message ? (
        <InlineNotice
          tone={message.startsWith("Đã") ? "success" : "warning"}
          title={message}
        />
      ) : null}

      <InlineNotice
        tone={workspaceTone(workspaceStatus)}
        title={workspaceLabels[workspaceStatus]}
      >
        {workspaceStatus === "INCOMPLETE"
          ? "Hoàn tất hồ sơ thuế để hệ thống xác định đúng chế độ kỳ kê khai. Các trường hồ sơ doanh nghiệp vẫn là dữ liệu hỗ trợ và không khóa tính năng bán hàng."
          : workspaceStatus === "BLOCKED_BY_COMPLIANCE"
            ? "Hồ sơ thuế đã có nhưng chưa có rule pháp lý VERIFIED phù hợp. BLedger không tự suy đoán tỷ lệ, ngưỡng hoặc hạn kê khai."
            : workspaceStatus === "REVIEW_REQUIRED"
              ? "Có dữ liệu vận hành cần đối soát trước khi chuyển kỳ sang trạng thái kê khai hoặc khóa."
              : "Hồ sơ thuế, dữ liệu vận hành và rule VERIFIED hiện đáp ứng điều kiện rà soát."}
      </InlineNotice>

      <Card className="hero-metric compliance-hero">
        <span>Doanh thu bán hàng tháng hiện tại</span>
        <Money value={summary?.recordedRevenue ?? 0} className="money-hero" />
        <small>
          Doanh thu sau trả hàng trong tháng lịch hiện tại — không phải số thuế phải nộp.
        </small>
      </Card>

      <section>
        <div className="section-title">
          <div>
            <CheckCircle2 size={20} />
            <h2>Tính toàn vẹn dữ liệu</h2>
          </div>
        </div>
        <Card className="check-list">
          <div>
            <CheckCircle2 />
            <span>Thanh toán chờ xử lý</span>
            <StatusBadge
              tone={summary?.dataQuality.pendingPayments ? "warning" : "success"}
            >
              {summary?.dataQuality.pendingPayments ?? 0}
            </StatusBadge>
          </div>
          <div>
            <CheckCircle2 />
            <span>Sản phẩm tồn kho âm</span>
            <StatusBadge
              tone={summary?.dataQuality.negativeStock ? "danger" : "success"}
            >
              {summary?.dataQuality.negativeStock ?? 0}
            </StatusBadge>
          </div>
        </Card>
      </section>

      <section>
        <div className="section-title">
          <div>
            <FileCheck2 size={20} />
            <h2>Tax Compliance</h2>
          </div>
          <StatusBadge tone={workspaceTone(workspaceStatus)}>
            {workspaceLabels[workspaceStatus]}
          </StatusBadge>
        </div>
        <Card className="check-list">
          <div>
            <ShieldCheck />
            <span>Hồ sơ người nộp thuế</span>
            <StatusBadge tone={profile.configured ? "success" : "warning"}>
              {profile.configured ? "Đã cấu hình" : "Chưa hoàn tất"}
            </StatusBadge>
          </div>
          <div>
            <Landmark />
            <span>Rule pháp lý VERIFIED phù hợp</span>
            <StatusBadge tone={rules.length ? "success" : "warning"}>
              {rules.length}
            </StatusBadge>
          </div>
          <div>
            <Building2 />
            <span>Hồ sơ doanh nghiệp hỗ trợ kê khai</span>
            <StatusBadge
              tone={
                (summary?.businessProfile?.percent ?? 0) === 100
                  ? "success"
                  : (summary?.businessProfile?.percent ?? 0) > 0
                    ? "info"
                    : "neutral"
              }
            >
              {summary?.businessProfile?.percent ?? 0}% hoàn tất
            </StatusBadge>
          </div>
        </Card>
        <small>
          Hồ sơ doanh nghiệp là dữ liệu hỗ trợ.{" "}
          <Link to="/settings">Bổ sung tại Thiết lập cửa hàng</Link>
        </small>
      </section>

      <form className="card product-form" onSubmit={saveProfile}>
        <div className="form-section-title">
          <Settings2 size={20} />
          <div>
            <h2>Hồ sơ thuế</h2>
            <small>Chỉ các trường này quyết định trạng thái cấu hình thuế của workspace.</small>
          </div>
        </div>

        <div className="form-grid">
          <label>
            Loại hình người nộp thuế
            <select
              value={profile.taxpayerType}
              onChange={(event) =>
                setProfile((current) => ({
                  ...current,
                  taxpayerType: event.target.value as TaxpayerType,
                }))
              }
              disabled={!canEditProfile}
            >
              {Object.entries(taxpayerLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Phương pháp VAT
            <select
              value={profile.vatMethod}
              onChange={(event) =>
                setProfile((current) => ({
                  ...current,
                  vatMethod: event.target.value as VatMethod,
                }))
              }
              disabled={!canEditProfile}
            >
              {Object.entries(vatLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Chế độ khai thuế
            <select
              value={profile.filingCadence}
              onChange={(event) =>
                setProfile((current) => ({
                  ...current,
                  filingCadence: event.target.value as FilingCadence,
                }))
              }
              disabled={!canEditProfile}
            >
              {Object.entries(cadenceLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Ngành / lĩnh vực kinh doanh
            <input
              value={profile.businessSector ?? ""}
              onChange={(event) =>
                setProfile((current) => ({
                  ...current,
                  businessSector: event.target.value,
                }))
              }
              disabled={!canEditProfile}
              placeholder="Ví dụ: Bán lẻ hàng tạp hóa"
            />
          </label>
        </div>

        {canEditProfile ? (
          <button className="btn btn-primary" disabled={busy}>
            <Save size={18} />
            {busy ? "Đang lưu..." : "Lưu hồ sơ thuế"}
          </button>
        ) : null}
      </form>

      <section>
        <div className="section-title">
          <div>
            <Landmark size={20} />
            <h2>Căn cứ pháp lý đang hiệu lực</h2>
          </div>
        </div>

        {rules.length === 0 ? (
          <InlineNotice tone="warning" title="Chưa có rule VERIFIED phù hợp">
            BLedger không tự suy đoán tỷ lệ, ngưỡng hay deadline. Chỉ rule có
            nguồn, ngày hiệu lực và trạng thái VERIFIED mới được đưa vào workspace.
          </InlineNotice>
        ) : (
          <div className="card-list">
            {rules.map((rule) => (
              <Card key={rule.id}>
                <strong>{rule.name}</strong>
                <p>
                  {rule.code} · phiên bản {rule.version}
                </p>
                <p>{rule.legalReference ?? "Chưa có mô tả căn cứ"}</p>
                {rule.sourceUrl ? (
                  <a href={rule.sourceUrl} target="_blank" rel="noreferrer">
                    Xem nguồn pháp lý
                  </a>
                ) : null}
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="section-title">
          <div>
            <CalendarRange size={20} />
            <h2>Kỳ kê khai</h2>
          </div>
        </div>

        {!profile.configured ? (
          <InlineNotice tone="warning" title="Chưa thể tạo kỳ">
            Hoàn tất loại hình người nộp thuế, phương pháp VAT và chế độ khai thuế trước.
            Hệ thống sẽ tự sinh mã kỳ và ngày bắt đầu/kết thúc để tránh nhập sai.
          </InlineNotice>
        ) : null}

        {canManagePeriods ? (
          <form className="card product-form" onSubmit={createPeriod}>
            <div className="form-grid">
              <label>
                Loại kỳ
                <select
                  value={periodType}
                  onChange={(event) => setPeriodType(event.target.value as TaxPeriodType)}
                >
                  <option value="MONTH">Tháng</option>
                  <option value="QUARTER">Quý</option>
                  <option value="YEAR">Năm</option>
                  <option value="OTHER">Khoảng thời gian khác</option>
                </select>
              </label>

              {periodType !== "OTHER" ? (
                <label>
                  Năm
                  <input
                    type="number"
                    min={1900}
                    max={2200}
                    value={periodYear}
                    onChange={(event) => setPeriodYear(Number(event.target.value))}
                  />
                </label>
              ) : null}

              {periodType === "MONTH" ? (
                <label>
                  Tháng
                  <select
                    value={periodMonth}
                    onChange={(event) => setPeriodMonth(Number(event.target.value))}
                  >
                    {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                      <option key={month} value={month}>
                        Tháng {month}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {periodType === "QUARTER" ? (
                <label>
                  Quý
                  <select
                    value={periodQuarter}
                    onChange={(event) => setPeriodQuarter(Number(event.target.value))}
                  >
                    {[1, 2, 3, 4].map((quarter) => (
                      <option key={quarter} value={quarter}>
                        Quý {quarter}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {periodType === "OTHER" ? (
                <>
                  <label>
                    Từ ngày
                    <input
                      type="date"
                      value={customStart}
                      onChange={(event) => setCustomStart(event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Đến ngày
                    <input
                      type="date"
                      value={customEnd}
                      min={customStart || undefined}
                      onChange={(event) => setCustomEnd(event.target.value)}
                      required
                    />
                  </label>
                </>
              ) : null}
            </div>

            <div className="setting-row">
              <span>
                <strong>Mã kỳ tự động: {preview.code}</strong>
                <small>
                  {preview.startDate || "—"} → {preview.endDate || "—"}
                </small>
              </span>
              <StatusBadge tone="info">Hệ thống tự tính</StatusBadge>
            </div>

            <button
              className="btn btn-primary"
              disabled={
                busy ||
                !profile.configured ||
                (periodType === "OTHER" &&
                  (!customStart || !customEnd || customEnd < customStart))
              }
            >
              Tạo kỳ
            </button>
          </form>
        ) : null}

        <div className="card-list">
          {periods.map((period) => {
            const next = nextPeriodStatus(period.status);
            return (
              <Card key={period.id}>
                <div className="setting-row">
                  <span>
                    <strong>{period.code}</strong>
                    <small>
                      {dateOnly(period.startDate)} → {dateOnly(period.endDate)}
                    </small>
                  </span>
                  <StatusBadge tone={statusTone(period.status)}>
                    {periodStatusLabels[period.status]}
                  </StatusBadge>
                </div>
                {next && canManagePeriods ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy}
                    onClick={() => void transitionPeriod(period)}
                  >
                    Chuyển sang {periodStatusLabels[next]}
                  </button>
                ) : null}
              </Card>
            );
          })}
        </div>
      </section>

      <InlineNotice tone="info" title="Không tự nộp tờ khai">
        Workspace hiện chỉ tổng hợp, kiểm tra, khóa kỳ và lưu audit trail.
        Việc tính hoặc nộp nghĩa vụ thuế chỉ được mở khi có bộ rule pháp lý
        đã xác minh và quy trình nghiệm thu riêng.
      </InlineNotice>
    </div>
  );
}
